import React, { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import Swal from "sweetalert2";
import { fetchThemeData } from "../../themeSlice";
import { updateThemeData } from "../../functions/updateThemeData";
import { apiUrl } from "../../../utils/apiUrl";

const MIN_ROUNDS = 1;
const MAX_ROUNDS = 16;

const ROUND_TYPES = [
  { value: "morse", label: "Morse code" },
  { value: "shapeCipher", label: "Shape cipher" },
  { value: "coordinates", label: "Coordinates" },
  { value: "pigpen", label: "Pigpen cipher" },
  { value: "blurry", label: "Blurry image" },
  { value: "jigsaw", label: "Jigsaw puzzle" },
  { value: "unscramble", label: "Unscramble" },
  { value: "maze", label: "Maze" },
];

const IMAGE_EXT = new Set(["jpg", "jpeg", "png", "gif"]);
const AUDIO_EXT = new Set(["mp3"]);

const MAZE_LEVELS = [
  { value: "easy", label: "Easy" },
  { value: "medium", label: "Medium" },
  { value: "hard", label: "Hard" },
];

function normalizeLevel(value) {
  const v = String(value || "").trim().toLowerCase();
  return MAZE_LEVELS.some((l) => l.value === v) ? v : "medium";
}

function typeLabel(type) {
  return ROUND_TYPES.find((t) => t.value === type)?.label || type;
}

function normalizeRound(raw, index) {
  const type = String(raw?.type || "morse");
  const popup = raw?.popup && typeof raw.popup === "object" ? raw.popup : {};
  const base = {
    enabled: raw?.enabled !== false,
    roundNumber: Number(raw?.roundNumber) || index + 1,
    state: String(raw?.state || "").trim(),
    bannerColor: String(raw?.bannerColor || "#5a7a1e"),
    title: String(raw?.title || "").trim(),
    backgroundTheme: String(raw?.backgroundTheme || "").trim(),
    type,
    popup: {
      heading: String(popup.heading || "").trim(),
      body: String(popup.body || "").trim(),
    },
  };

  if (type === "maze") {
    return {
      ...base,
      answer: null,
      mazeImage: String(raw?.mazeImage || "").trim(),
      mazeEntry: String(raw?.mazeEntry || "").trim(),
      mazeExit: String(raw?.mazeExit || "").trim(),
      gamePlayLabel: normalizeLevel(raw?.gamePlayLabel),
    };
  }

  const round = {
    ...base,
    answer: raw?.answer == null ? "" : String(raw.answer).trim(),
  };

  switch (type) {
    case "morse":
      return {
        ...round,
        audio: String(raw?.audio || "").trim(),
        image_left: String(raw?.image_left || "").trim(),
        image_right: String(raw?.image_right || "").trim(),
      };
    case "shapeCipher":
      return {
        ...round,
        image_left: String(raw?.image_left || "").trim(),
        image_right: String(raw?.image_right || "").trim(),
      };
    case "coordinates":
      return {
        ...round,
        image_left: String(raw?.image_left || "").trim(),
        map_embed: String(raw?.map_embed || "").trim(),
        gps_code: String(raw?.gps_code || "").trim(),
      };
    case "pigpen":
      return {
        ...round,
        image_left: String(raw?.image_left || "").trim(),
        image_right: String(raw?.image_right || "").trim(),
        image_bottom: String(raw?.image_bottom || "").trim(),
      };
    case "blurry":
      return {
        ...round,
        image: String(raw?.image || "").trim(),
        initialBlurPx: Number(raw?.initialBlurPx) || 12,
      };
    case "jigsaw":
      return {
        ...round,
        jigsawimages: String(raw?.jigsawimages || "").trim(),
        jigsawCols: Number(raw?.jigsawCols) || 3,
        jigsawRows: Number(raw?.jigsawRows) || 3,
      };
    case "unscramble":
      return {
        ...round,
        image: String(raw?.image || "").trim(),
        scrambledWord: String(raw?.scrambledWord || "").trim(),
      };
    default:
      return round;
  }
}

function normalizeRounds(raw) {
  if (!Array.isArray(raw)) return [];
  return raw.map((r, i) => normalizeRound(r, i)).map((r, i) => ({ ...r, roundNumber: i + 1 }));
}

function emptyDraft(type, roundNumber) {
  const base = {
    enabled: true,
    roundNumber,
    state: "",
    bannerColor: "#5a7a1e",
    title: "",
    backgroundTheme: "",
    type,
    answer: "",
    popupHeading: "",
    popupBody: "",
    audio: "",
    image_left: "",
    image_right: "",
    image_bottom: "",
    image: "",
    initialBlurPx: 12,
    jigsawimages: "",
    jigsawCols: 3,
    jigsawRows: 3,
    scrambledWord: "",
    mazeImage: "",
    mazeEntry: "",
    mazeExit: "",
    gamePlayLabel: "medium",
    map_embed: "",
    gps_code: "",
  };
  return base;
}

function roundToDraft(round) {
  if (!round) return emptyDraft("morse", 1);
  return {
    enabled: round.enabled !== false,
    roundNumber: round.roundNumber,
    state: round.state || "",
    bannerColor: round.bannerColor || "#5a7a1e",
    title: round.title || "",
    backgroundTheme: round.backgroundTheme || "",
    type: round.type || "morse",
    answer: round.answer == null ? "" : String(round.answer),
    popupHeading: round.popup?.heading || "",
    popupBody: round.popup?.body || "",
    audio: round.audio || "",
    image_left: round.image_left || "",
    image_right: round.image_right || "",
    image_bottom: round.image_bottom || "",
    image: round.image || "",
    initialBlurPx: Number(round.initialBlurPx) || 12,
    jigsawimages: round.jigsawimages || "",
    jigsawCols: Number(round.jigsawCols) || 3,
    jigsawRows: Number(round.jigsawRows) || 3,
    scrambledWord: round.scrambledWord || "",
    mazeImage: round.mazeImage || "",
    mazeEntry: round.mazeEntry || "",
    mazeExit: round.mazeExit || "",
    gamePlayLabel: normalizeLevel(round.gamePlayLabel),
    map_embed: round.map_embed || "",
    gps_code: round.gps_code || "",
  };
}

function draftToRound(draft) {
  const type = draft.type;
  const base = {
    enabled: !!draft.enabled,
    roundNumber: Number(draft.roundNumber),
    state: String(draft.state || "").trim(),
    bannerColor: String(draft.bannerColor || "#5a7a1e"),
    title: String(draft.title || "").trim(),
    backgroundTheme: String(draft.backgroundTheme || "").trim(),
    type,
    popup: {
      heading: String(draft.popupHeading || "").trim(),
      body: String(draft.popupBody || "").trim(),
    },
  };

  if (type === "maze") {
    return {
      ...base,
      answer: null,
      mazeImage: String(draft.mazeImage || "").trim(),
      mazeEntry: String(draft.mazeEntry || "").trim(),
      mazeExit: String(draft.mazeExit || "").trim(),
      gamePlayLabel: normalizeLevel(draft.gamePlayLabel),
    };
  }

  const round = {
    ...base,
    answer: String(draft.answer || "").trim().toLowerCase(),
  };

  switch (type) {
    case "morse":
      return {
        ...round,
        audio: String(draft.audio || "").trim(),
        image_left: String(draft.image_left || "").trim(),
        image_right: String(draft.image_right || "").trim(),
      };
    case "shapeCipher":
      return {
        ...round,
        image_left: String(draft.image_left || "").trim(),
        image_right: String(draft.image_right || "").trim(),
      };
    case "coordinates":
      return {
        ...round,
        image_left: String(draft.image_left || "").trim(),
        map_embed: String(draft.map_embed || "").trim(),
        gps_code: String(draft.gps_code || "").trim(),
      };
    case "pigpen":
      return {
        ...round,
        image_left: String(draft.image_left || "").trim(),
        image_right: String(draft.image_right || "").trim(),
        image_bottom: String(draft.image_bottom || "").trim(),
      };
    case "blurry":
      return {
        ...round,
        image: String(draft.image || "").trim(),
        initialBlurPx: Math.max(1, Number(draft.initialBlurPx) || 12),
      };
    case "jigsaw":
      return {
        ...round,
        jigsawimages: String(draft.jigsawimages || "").trim(),
        jigsawCols: Math.max(2, Number(draft.jigsawCols) || 3),
        jigsawRows: Math.max(2, Number(draft.jigsawRows) || 3),
      };
    case "unscramble":
      return {
        ...round,
        image: String(draft.image || "").trim(),
        scrambledWord: String(draft.scrambledWord || "").trim(),
      };
    default:
      return round;
  }
}

function getRoundPreviewImage(round) {
  if (!round) return "";
  switch (round.type) {
    case "morse":
    case "shapeCipher":
    case "pigpen":
      return round.image_right || round.image_left || round.image_bottom || "";
    case "coordinates":
      return round.image_left || "";
    case "blurry":
    case "unscramble":
      return round.image || "";
    case "jigsaw":
      return round.jigsawimages || "";
    case "maze":
      return round.mazeImage || "";
    default:
      return "";
  }
}

function RoundThumb({ src, resolveAssetUrl }) {
  const [failed, setFailed] = useState(false);
  const url = resolveAssetUrl(src);
  useEffect(() => setFailed(false), [src]);

  if (!url || failed) {
    return (
      <div className="catmgr-table-thumb-wrap">
        <div className="quiz-q-thumb-placeholder quiz-q-thumb-placeholder--game">
          <i className="fa-solid fa-map-location-dot" />
        </div>
      </div>
    );
  }
  return (
    <div className="catmgr-table-thumb-wrap">
      <img
        src={url}
        alt=""
        className="catmgr-table-thumb"
        referrerPolicy="no-referrer"
        onError={() => setFailed(true)}
      />
    </div>
  );
}

function AssetField({
  label,
  fieldKey,
  value,
  onChange,
  onUpload,
  uploading,
  resolveAssetUrl,
  accept,
  isAudio,
}) {
  const inputRef = useRef(null);
  const url = !isAudio ? resolveAssetUrl(value) : "";

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const filename = await onUpload(file, { isAudio });
      onChange(fieldKey, filename);
      Swal.fire("Uploaded", `${label} updated.`, "success");
    } catch (err) {
      Swal.fire("Upload failed", err.message || "Could not upload file.", "error");
    }
    if (inputRef.current) inputRef.current.value = "";
  };

  return (
    <div className="quiz-round-field" style={{ marginBottom: 14 }}>
      <label className="quiz-field-label">{label}</label>
      <div className="quiz-q-image-row">
        {!isAudio && (
          url ? (
            <img src={url} alt="" className="quiz-q-thumb" referrerPolicy="no-referrer" />
          ) : (
            <div className="quiz-q-thumb-placeholder">
              <i className="fa-solid fa-image" />
            </div>
          )
        )}
        {isAudio && value ? (
          <span className="catmgr-cat-key" style={{ fontSize: 12 }}>
            <i className="fa-solid fa-volume-high" /> {value}
          </span>
        ) : null}
        <input
          className="catmgr-modal-input quiz-filename-input"
          value={value}
          onChange={(e) => onChange(fieldKey, e.target.value)}
          placeholder="filename.ext"
        />
        <button
          type="button"
          className="catmgr-btn catmgr-btn-outline catmgr-btn-sm"
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
        >
          {uploading ? "Uploading…" : "Upload"}
        </button>
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          className="quiz-file-input"
          onChange={handleFile}
        />
      </div>
    </div>
  );
}

export default function RoundsEditor({ data, currentTheme, adminToken, gameName, onBack }) {
  const dispatch = useDispatch();
  const [rounds, setRounds] = useState([]);
  const [saving, setSaving] = useState(false);
  const [modal, setModal] = useState(null);
  const [draft, setDraft] = useState(emptyDraft("morse", 1));
  const [uploadingField, setUploadingField] = useState("");

  const S3 = process.env.REACT_APP_S3_PATH || "";
  const resolveAssetUrl = (path) => {
    const raw = String(path || "").trim();
    if (!raw) return "";
    if (/^https?:\/\//i.test(raw)) return raw;
    const rel = raw.replace(/^\/+/, "");
    return `${S3}${encodeURIComponent(rel).replace(/%2F/g, "/")}`;
  };

  useEffect(() => {
    if (!data) return;
    if ("rounds" in data) {
      setRounds(normalizeRounds(data.rounds));
    }
  }, [data]);

  const persistRounds = async (nextRounds, successMessage = "Rounds updated.") => {
    if (!currentTheme) {
      Swal.fire("No theme", "Select a theme first.", "error");
      return false;
    }
    const normalized = normalizeRounds(nextRounds);
    setSaving(true);
    try {
      await updateThemeData({
        payload: {
          data: { rounds: normalized },
          currentTheme,
        },
        token: adminToken,
      });
      await dispatch(fetchThemeData({ themeId: currentTheme })).unwrap();
      setRounds(normalized);
      Swal.fire("Saved", successMessage, "success");
      return true;
    } catch (e) {
      Swal.fire("Error", e.message || "Save failed.", "error");
      return false;
    } finally {
      setSaving(false);
    }
  };

  const uploadAsset = async (file, { isAudio }) => {
    const ext = file.name.split(".").pop().toLowerCase();
    const allowed = isAudio ? AUDIO_EXT : IMAGE_EXT;
    if (!allowed.has(ext)) {
      throw new Error(
        isAudio ? "Only MP3 files are allowed for audio." : "Only JPG, PNG, and GIF are allowed for images."
      );
    }
    const maxBytes = isAudio ? 15 * 1024 * 1024 : 5 * 1024 * 1024;
    if (file.size > maxBytes) {
      throw new Error(isAudio ? "Maximum audio size is 15 MB." : "Maximum image size is 5 MB.");
    }
    const fd = new FormData();
    fd.append("file", file);
    fd.append("themeName", String(currentTheme));
    const res = await fetch(apiUrl("/uploadCategoryImage"), {
      method: "POST",
      headers: { Authorization: `Bearer ${adminToken}` },
      body: fd,
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Upload failed");
    return json.filename;
  };

  const handleUploadForField = async (file, { isAudio }, fieldKey) => {
    setUploadingField(fieldKey);
    try {
      return await uploadAsset(file, { isAudio });
    } finally {
      setUploadingField("");
    }
  };

  const setDraftField = (key, value) => {
    setDraft((prev) => ({ ...prev, [key]: value }));
  };

  const openAddModal = () => {
    if (rounds.length >= MAX_ROUNDS) {
      Swal.fire("Limit reached", `Maximum ${MAX_ROUNDS} rounds allowed.`, "warning");
      return;
    }
    setModal({ mode: "add" });
    setDraft(emptyDraft("morse", rounds.length + 1));
  };

  const openEditModal = (index) => {
    setModal({ mode: "edit", index });
    setDraft(roundToDraft(rounds[index]));
  };

  const closeModal = () => {
    setModal(null);
    setDraft(emptyDraft("morse", 1));
  };

  const trimmedLen = (v) => String(v ?? "").trim().length;

  const validateDraft = () => {
    if (!String(draft.state || "").trim()) {
      Swal.fire("Required", "State name is required.", "warning");
      return false;
    }
    if (trimmedLen(draft.state) > 30) {
      Swal.fire("Too long", "State max length is 30 characters.", "warning");
      return false;
    }
    if (!String(draft.title || "").trim()) {
      Swal.fire("Required", "Round title is required.", "warning");
      return false;
    }
    if (trimmedLen(draft.title) > 100) {
      Swal.fire("Too long", "Round title max length is 100 characters.", "warning");
      return false;
    }
    if (!String(draft.backgroundTheme || "").trim()) {
      Swal.fire("Required", "Background theme key is required.", "warning");
      return false;
    }
    if (draft.type !== "maze" && !String(draft.answer || "").trim()) {
      Swal.fire("Required", "Answer is required for this round type.", "warning");
      return false;
    }
    if (draft.type !== "maze" && trimmedLen(draft.answer) > 50) {
      Swal.fire("Too long", "Correct answer max length is 50 characters.", "warning");
      return false;
    }
    if (!String(draft.popupHeading || "").trim() || !String(draft.popupBody || "").trim()) {
      Swal.fire("Required", "Popup heading and body are required.", "warning");
      return false;
    }
    if (trimmedLen(draft.popupHeading) > 50) {
      Swal.fire("Too long", "Heading max length is 50 characters.", "warning");
      return false;
    }
    if (trimmedLen(draft.popupBody) > 300) {
      Swal.fire("Too long", "Body max length is 300 characters.", "warning");
      return false;
    }
    return true;
  };

  const handleSaveRound = async () => {
    if (!modal) return;
    if (!validateDraft()) return;

    const built = draftToRound(draft);
    let nextRounds;
    if (modal.mode === "add") {
      nextRounds = [...rounds, built];
    } else {
      nextRounds = rounds.map((r, i) => (i === modal.index ? built : r));
    }
    nextRounds = normalizeRounds(nextRounds);
    const ok = await persistRounds(
      nextRounds,
      modal.mode === "add" ? "Round added." : "Round updated."
    );
    if (ok) closeModal();
  };

  const handleDeleteRound = (index) => {
    if (rounds.length <= MIN_ROUNDS) {
      Swal.fire("Cannot delete", `At least ${MIN_ROUNDS} round is required.`, "warning");
      return;
    }
    const round = rounds[index];
    Swal.fire({
      title: "Delete round?",
      text: `Round ${round.roundNumber} (${round.state}) will be removed.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete",
    }).then(async (r) => {
      if (!r.isConfirmed) return;
      const nextRounds = normalizeRounds(rounds.filter((_, i) => i !== index));
      await persistRounds(nextRounds, "Round deleted.");
    });
  };

  const handleTypeChange = (type) => {
    setDraft((prev) => {
      const fresh = emptyDraft(type, prev.roundNumber);
      return {
        ...fresh,
        enabled: prev.enabled,
        state: prev.state,
        bannerColor: prev.bannerColor,
        title: prev.title,
        backgroundTheme: prev.backgroundTheme,
        answer: type === "maze" ? "" : prev.answer,
        popupHeading: prev.popupHeading,
        popupBody: prev.popupBody,
        type,
      };
    });
  };

  const renderTypeFields = () => {
    const uploadProps = (label, fieldKey, accept, isAudio = false) => (
      <AssetField
        key={fieldKey}
        label={label}
        fieldKey={fieldKey}
        value={draft[fieldKey]}
        onChange={setDraftField}
        uploading={uploadingField === fieldKey}
        onUpload={(file, opts) => handleUploadForField(file, opts, fieldKey)}
        resolveAssetUrl={resolveAssetUrl}
        accept={accept}
        isAudio={isAudio}
      />
    );

    switch (draft.type) {
      case "morse":
        return (
          <>
            {uploadProps("Audio (MP3)", "audio", ".mp3", true)}
            {uploadProps("Left image", "image_left", ".jpg,.jpeg,.png,.gif")}
            {uploadProps("Right image", "image_right", ".jpg,.jpeg,.png,.gif")}
          </>
        );
      case "shapeCipher":
        return (
          <>
            {uploadProps("Left image", "image_left", ".jpg,.jpeg,.png,.gif")}
            {uploadProps("Right image", "image_right", ".jpg,.jpeg,.png,.gif")}
          </>
        );
      case "coordinates":
        return (
          <>
            {uploadProps("Left image (optional)", "image_left", ".jpg,.jpeg,.png,.gif")}
            <div className="quiz-round-field" style={{ marginTop: 12 }}>
              <label className="quiz-field-label">Google Maps embed URL</label>
              <textarea
                className="quiz-textarea"
                rows={3}
                value={draft.map_embed}
                onChange={(e) => setDraftField("map_embed", e.target.value)}
                placeholder="https://www.google.com/maps/embed?pb=..."
              />
              <span className="quiz-field-hint">Paste the Google Maps embed link (iframe src)</span>
            </div>
            <div className="quiz-round-field" style={{ marginTop: 12 }}>
              <label className="quiz-field-label">GPS code</label>
              <input
                className="catmgr-modal-input"
                value={draft.gps_code}
                maxLength={80}
                onChange={(e) => setDraftField("gps_code", e.target.value)}
                placeholder="e.g. 30.3462° N, 79.0485° E"
              />
              <span className="quiz-field-hint">Shown above the map on the game page</span>
            </div>
          </>
        );
      case "pigpen":
        return (
          <>
            {uploadProps("Left image", "image_left", ".jpg,.jpeg,.png,.gif")}
            {uploadProps("Right image", "image_right", ".jpg,.jpeg,.png,.gif")}
            {uploadProps("Bottom image (chart)", "image_bottom", ".jpg,.jpeg,.png,.gif")}
          </>
        );
      case "blurry":
        return (
          <>
            {uploadProps("Image", "image", ".jpg,.jpeg,.png,.gif")}
            <div className="quiz-round-field">
              <label className="quiz-field-label">Initial blur (px)</label>
              <input
                type="number"
                min={1}
                max={40}
                className="catmgr-modal-input"
                value={draft.initialBlurPx}
                onChange={(e) => setDraftField("initialBlurPx", e.target.value)}
              />
            </div>
          </>
        );
      case "jigsaw":
        return (
          <>
            {uploadProps("Jigsaw source image", "jigsawimages", ".jpg,.jpeg,.png,.gif")}
            <div className="quiz-options-grid">
              <div className="quiz-round-field">
                <label className="quiz-field-label">Columns</label>
                <input
                  type="number"
                  min={2}
                  max={6}
                  className="catmgr-modal-input"
                  value={draft.jigsawCols}
                  onChange={(e) => setDraftField("jigsawCols", e.target.value)}
                />
              </div>
              <div className="quiz-round-field">
                <label className="quiz-field-label">Rows</label>
                <input
                  type="number"
                  min={2}
                  max={6}
                  className="catmgr-modal-input"
                  value={draft.jigsawRows}
                  onChange={(e) => setDraftField("jigsawRows", e.target.value)}
                />
              </div>
            </div>
          </>
        );
      case "unscramble":
        return (
          <>
            {uploadProps("Image", "image", ".jpg,.jpeg,.png,.gif")}
            <div className="quiz-round-field">
              <label className="quiz-field-label">Scrambled word</label>
              <input
                className="catmgr-modal-input"
                value={draft.scrambledWord}
                onChange={(e) => setDraftField("scrambledWord", e.target.value)}
                placeholder="e.g. AJOHHS"
              />
            </div>
          </>
        );
      case "maze":
        return (
          <>
            {uploadProps("Maze image", "mazeImage", ".jpg,.jpeg,.png,.gif")}
            <div className="quiz-options-grid">
              <div className="quiz-round-field">
                <label className="quiz-field-label">Entry point</label>
                <input
                  className="catmgr-modal-input"
                  value={draft.mazeEntry}
                  onChange={(e) => setDraftField("mazeEntry", e.target.value)}
                />
              </div>
              <div className="quiz-round-field">
                <label className="quiz-field-label">Exit point</label>
                <input
                  className="catmgr-modal-input"
                  value={draft.mazeExit}
                  onChange={(e) => setDraftField("mazeExit", e.target.value)}
                />
              </div>
            </div>
            <div className="quiz-round-field" style={{ marginTop: 12 }}>
              <label className="quiz-field-label">Difficulty level</label>
              <select
                className="quiz-prompt-select"
                style={{ width: "100%" }}
                value={normalizeLevel(draft.gamePlayLabel)}
                onChange={(e) => setDraftField("gamePlayLabel", e.target.value)}
              >
                {MAZE_LEVELS.map((l) => (
                  <option key={l.value} value={l.value}>
                    {l.label}
                  </option>
                ))}
              </select>
            </div>
          </>
        );
      default:
        return null;
    }
  };

  return (
    <>
      <div className="back-button-holder">
        <button type="button" className="back-button" onClick={onBack}>
          <i className="fa-solid fa-arrow-left" /> Back
        </button>
      </div>

      <div className="catmgr-page">
        <div className="catmgr-header">
          <div className="catmgr-header-left">
            <h2 className="catmgr-title">
              <i className="fa-solid fa-route" /> &nbsp;{gameName} — Game Rounds
            </h2>
            <p className="catmgr-subtitle">
              {rounds.length} / {MAX_ROUNDS} rounds &nbsp;·&nbsp; Configure puzzle stages for Travel India
            </p>
          </div>
          <div className="catmgr-header-right">
            <button
              type="button"
              className="catmgr-btn catmgr-btn-outline"
              onClick={openAddModal}
              disabled={saving}
            >
              <i className="fa-solid fa-plus" /> Add Round
            </button>
          </div>
        </div>

        <div className="quiz-rounds-editor">
          <div className="quiz-rounds-editor-toolbar">
            <p className="quiz-rounds-editor-hint">
              Each round is a travel puzzle stage. Edit state, challenge type, assets, correct answer, and
              the reveal popup shown when players succeed.
            </p>
          </div>

          {rounds.length === 0 ? (
            <div className="catmgr-empty">
              No rounds yet. Click <strong>Add Round</strong> to create one.
            </div>
          ) : (
            <div className="catmgr-card">
              <div className="catmgr-table-wrap">
                <table className="catmgr-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Preview</th>
                      <th>State</th>
                      <th>Type</th>
                      <th>Title</th>
                      <th>Answer</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rounds.map((round, index) => (
                      <tr key={`${round.roundNumber}-${index}`}>
                        <td>{round.roundNumber}</td>
                        <td>
                          <RoundThumb
                            src={getRoundPreviewImage(round)}
                            resolveAssetUrl={resolveAssetUrl}
                          />
                        </td>
                        <td>
                          <strong>{round.state}</strong>
                          <div className="catmgr-cat-key">{round.backgroundTheme}</div>
                        </td>
                        <td>
                          <span className="catmgr-badge">{typeLabel(round.type)}</span>
                        </td>
                        <td style={{ maxWidth: 220 }}>{round.title}</td>
                        <td>{round.type === "maze" ? "—" : round.answer || "—"}</td>
                        <td>
                          <span
                            className={`catmgr-badge${round.enabled ? "" : ""}`}
                            style={
                              round.enabled
                                ? { background: "#dcfce7", color: "#166534" }
                                : { background: "#f1f5f9", color: "#64748b" }
                            }
                          >
                            {round.enabled ? "Enabled" : "Disabled"}
                          </span>
                        </td>
                        <td>
                          <div className="catmgr-table-actions">
                            <button
                              type="button"
                              className="catmgr-icon-btn catmgr-icon-btn-sm"
                              title="Edit"
                              onClick={() => openEditModal(index)}
                              disabled={saving}
                            >
                              <i className="fa-solid fa-pen" />
                            </button>
                            <button
                              type="button"
                              className="catmgr-icon-btn catmgr-icon-btn-sm catmgr-icon-btn-danger"
                              title="Delete"
                              onClick={() => handleDeleteRound(index)}
                              disabled={saving}
                            >
                              <i className="fa-solid fa-trash" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>

      {modal && (
        <>
          <div className="catmgr-backdrop" onClick={closeModal} />
          <div className="catmgr-modal catmgr-modal-wide">
            <h3>{modal.mode === "add" ? "Add Round" : `Edit Round ${draft.roundNumber}`}</h3>

            <div className="quiz-options-grid">
              <div className="quiz-round-field">
                <label className="quiz-field-label">Challenge type</label>
                <select
                  className="quiz-prompt-select"
                  style={{ width: "100%" }}
                  value={draft.type}
                  onChange={(e) => handleTypeChange(e.target.value)}
                >
                  {ROUND_TYPES.map((t) => (
                    <option key={t.value} value={t.value}>
                      {t.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="quiz-round-field">
                <label className="quiz-field-label">Enabled</label>
                <select
                  className="quiz-prompt-select"
                  style={{ width: "100%" }}
                  value={draft.enabled ? "yes" : "no"}
                  onChange={(e) => setDraftField("enabled", e.target.value === "yes")}
                >
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                </select>
              </div>
            </div>

            <div className="quiz-options-grid" style={{ marginTop: 12 }}>
              <div className="quiz-round-field">
                <label className="quiz-field-label">State</label>
                <input
                  className="catmgr-modal-input"
                  value={draft.state}
                  maxLength={30}
                  onChange={(e) => setDraftField("state", e.target.value)}
                  placeholder="e.g. Kerala"
                />
                <span className="quiz-field-hint">Max 30 characters</span>
              </div>
              <div className="quiz-round-field">
                <label className="quiz-field-label">Background theme key</label>
                <input
                  className="catmgr-modal-input"
                  value={draft.backgroundTheme}
                  onChange={(e) => setDraftField("backgroundTheme", e.target.value)}
                  placeholder="e.g. kerala"
                />
              </div>
            </div>

            <div className="quiz-round-field" style={{ marginTop: 12 }}>
              <label className="quiz-field-label">Banner color</label>
              <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                <input
                  type="color"
                  value={draft.bannerColor}
                  onChange={(e) => setDraftField("bannerColor", e.target.value)}
                />
                <input
                  className="catmgr-modal-input"
                  value={draft.bannerColor}
                  onChange={(e) => setDraftField("bannerColor", e.target.value)}
                />
              </div>
            </div>

            <div className="quiz-round-field" style={{ marginTop: 12 }}>
              <label className="quiz-field-label">Round title</label>
              <textarea
                className="quiz-textarea"
                rows={2}
                value={draft.title}
                maxLength={100}
                onChange={(e) => setDraftField("title", e.target.value)}
              />
              <span className="quiz-field-hint">Max 100 characters</span>
            </div>

            {draft.type !== "maze" && (
              <div className="quiz-round-field" style={{ marginTop: 12 }}>
                <label className="quiz-field-label">Correct answer</label>
                <input
                  className="catmgr-modal-input"
                  value={draft.answer}
                  maxLength={50}
                  onChange={(e) => setDraftField("answer", e.target.value)}
                  placeholder="case-insensitive"
                />
                <span className="quiz-field-hint">Max 50 characters</span>
              </div>
            )}

            <div style={{ marginTop: 16, marginBottom: 8, fontWeight: 700, fontSize: 13 }}>
              Type-specific assets
            </div>
            {renderTypeFields()}

            <div style={{ marginTop: 16, marginBottom: 8, fontWeight: 700, fontSize: 13 }}>
              Success popup
            </div>
            <div className="quiz-round-field">
              <label className="quiz-field-label">Heading</label>
              <input
                className="catmgr-modal-input"
                value={draft.popupHeading}
                maxLength={50}
                onChange={(e) => setDraftField("popupHeading", e.target.value)}
              />
              <span className="quiz-field-hint">Max 50 characters</span>
            </div>
            <div className="quiz-round-field" style={{ marginTop: 10 }}>
              <label className="quiz-field-label">Body</label>
              <textarea
                className="quiz-textarea"
                rows={4}
                value={draft.popupBody}
                maxLength={300}
                onChange={(e) => setDraftField("popupBody", e.target.value)}
              />
              <span className="quiz-field-hint">Max 300 characters</span>
            </div>

            <div className="catmgr-modal-actions">
              <button type="button" onClick={closeModal}>
                Cancel
              </button>
              <button
                type="button"
                className="primary"
                onClick={handleSaveRound}
                disabled={saving || !!uploadingField}
              >
                {saving ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </>
      )}
    </>
  );
}
